import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Per DD §2.3 animation choreography 8-step sequence — which nodes lift
 * to full opacity at each step canonical preservation forward. Nodes not
 * listed at current step or earlier remain at low-emphasis (60% opacity)
 * per DD §2.3 Step 1 initial state canonical preservation forward.
 */
const NODE_ACTIVATION_BY_STEP = {
    0: [],
    1: ['substrate', 'patient'],
    2: ['substrate', 'patient'],
    3: ['substrate', 'patient', 'provider'],
    4: ['substrate', 'patient', 'provider', 'payer'],
    5: ['substrate', 'patient', 'provider', 'payer'],
    6: ['substrate', 'patient', 'provider', 'payer', 'sht', 'council'],
    7: ['substrate', 'patient', 'provider', 'payer', 'sht', 'council'],
};
export function FederationVisualization(props) {
    const { size = 720, animationStep, testId } = props;
    const cx = size / 2;
    const cy = size / 2;
    const substrateR = size * 0.08;
    const peripheralR = size * 0.055;
    const orbitR = size * 0.32;
    /**
     * When animationStep is undefined, render final-state diagram (all
     * nodes + edges at full visibility); reduced-motion compliant default.
     * When provided, drive 8-step choreography state per DD §2.3.
     */
    const isAnimated = animationStep !== undefined;
    const currentStep = animationStep ?? 7;
    const activeNodes = new Set(NODE_ACTIVATION_BY_STEP[currentStep]);
    const nodes = [
        { kind: 'substrate', label: 'Substrate', x: cx, y: cy, r: substrateR, variant: 'substrate' },
        {
            kind: 'patient',
            label: 'Patient (PHG)',
            x: cx,
            y: cy - orbitR,
            r: peripheralR,
            variant: 'peripheral',
        },
        {
            kind: 'provider',
            label: 'Christiana Care',
            x: cx - orbitR * 0.95,
            y: cy + orbitR * 0.31,
            r: peripheralR,
            variant: 'peripheral',
        },
        {
            kind: 'payer',
            label: 'Highmark BCBS DE',
            x: cx + orbitR * 0.95,
            y: cy + orbitR * 0.31,
            r: peripheralR,
            variant: 'peripheral',
        },
        {
            kind: 'sht',
            label: 'Smart Health Trust',
            x: cx + orbitR * 0.59,
            y: cy + orbitR * 0.81,
            r: peripheralR,
            variant: 'peripheral',
        },
        {
            kind: 'council',
            label: 'Smart Health Council',
            x: cx - orbitR * 0.59,
            y: cy + orbitR * 0.81,
            r: peripheralR,
            variant: 'peripheral',
        },
    ];
    const edges = [
        { from: 'patient', to: 'substrate', label: 'Authority routing', activateAtStep: 1 },
        { from: 'substrate', to: 'provider', label: 'Federated query', activateAtStep: 3 },
        { from: 'substrate', to: 'payer', label: 'Authority routing to payer', activateAtStep: 4 },
        { from: 'payer', to: 'substrate', label: 'Decision routing', activateAtStep: 5 },
        { from: 'substrate', to: 'sht', label: 'Audit chain emission (SHT)', activateAtStep: 6 },
        { from: 'substrate', to: 'council', label: 'Audit chain emission (Council)', activateAtStep: 6 },
    ];
    const nodeByKind = new Map(nodes.map((n) => [n.kind, n]));
    return (_jsxs("figure", { className: isAnimated
            ? 'federation-visualization federation-visualization--animated'
            : 'federation-visualization', "data-testid": testId ?? 'federation-visualization', "data-animation-step": isAnimated ? currentStep : undefined, role: "img", "aria-labelledby": "federation-visualization-caption", children: [_jsxs("svg", { viewBox: `0 0 ${size} ${size}`, className: "federation-visualization__svg", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("defs", { children: _jsx("marker", { id: "fv-arrow", viewBox: "0 0 10 10", refX: "9", refY: "5", markerWidth: "6", markerHeight: "6", orient: "auto-start-reverse", children: _jsx("path", { d: "M 0 0 L 10 5 L 0 10 z", className: "federation-visualization__arrowhead" }) }) }), _jsx("g", { className: "federation-visualization__edges", children: edges.map((e, i) => {
                            const from = nodeByKind.get(e.from);
                            const to = nodeByKind.get(e.to);
                            if (from === undefined || to === undefined)
                                return null;
                            const isActive = isAnimated ? currentStep >= e.activateAtStep : true;
                            return (_jsx("line", { x1: from.x, y1: from.y, x2: to.x, y2: to.y, className: isActive
                                    ? 'federation-visualization__edge federation-visualization__edge--active'
                                    : 'federation-visualization__edge federation-visualization__edge--inactive', "data-edge": `${e.from}-to-${e.to}`, "data-active": isActive, markerEnd: isActive ? 'url(#fv-arrow)' : undefined, children: _jsx("title", { children: e.label }) }, `${e.from}-${e.to}-${i}`));
                        }) }), _jsx("g", { className: "federation-visualization__nodes", children: nodes.map((n) => {
                            const isActive = isAnimated ? activeNodes.has(n.kind) : true;
                            return (_jsxs("g", { transform: `translate(${n.x} ${n.y})`, className: isActive
                                    ? `federation-visualization__node federation-visualization__node--${n.variant} federation-visualization__node--active`
                                    : `federation-visualization__node federation-visualization__node--${n.variant} federation-visualization__node--inactive`, "data-node": n.kind, "data-active": isActive, children: [_jsx("circle", { r: n.r, className: "federation-visualization__node-circle" }), _jsx("text", { textAnchor: "middle", dominantBaseline: "central", className: "federation-visualization__node-label", children: n.label })] }, n.kind));
                        }) })] }), _jsx("figcaption", { id: "federation-visualization-caption", className: "federation-visualization__caption", children: "Federated query-to-data architectural pattern \u2014 Smart Health Network routes authority, not data. All operations originate or terminate at the substrate node; institutional parties compose orthogonally per steady-state utility infrastructure language discipline." })] }));
}
//# sourceMappingURL=federation-visualization.js.map